// ── Priority Scale — Lake Vermont P1-P5 system ──
// P1 (lowest risk) → P5 (highest risk)
// 5-level color scale: green → blue → yellow → orange → red

export type PriorityLevel = "P1" | "P2" | "P3" | "P4" | "P5" | "Unknown";

/** Hex colors per priority level */
export const PRIORITY_HEX: Record<PriorityLevel, string> = {
  P1: "#22c55e",   // green-500 (lowest risk)
  P2: "#3b82f6",   // blue-500
  P3: "#eab308",   // yellow-500
  P4: "#f97316",   // orange-500
  P5: "#ef4444",   // red-500 (highest risk)
  Unknown: "#6b7280", // gray-500
};

/** RGBA colors per priority level (for deck.gl layers) */
export const PRIORITY_RGBA: Record<PriorityLevel, [number, number, number, number]> = {
  P1: [34, 197, 94, 200],
  P2: [59, 130, 246, 200],
  P3: [234, 179, 8, 200],
  P4: [249, 115, 22, 200],
  P5: [239, 68, 68, 200],
  Unknown: [156, 163, 175, 160],
};

/** Display labels for priority levels */
export const PRIORITY_LABELS: Record<PriorityLevel, string> = {
  P1: "P1 — Low",
  P2: "P2 — Moderate",
  P3: "P3 — Significant",
  P4: "P4 — High",
  P5: "P5 — Critical",
  Unknown: "Unknown",
};

/**
 * Normalize a priority value from various formats to PriorityLevel.
 * Handles: "P1", "p1", "1", "Priority 1", etc.
 */
export function normalizePriority(value: string | number | undefined): PriorityLevel {
  if (value === undefined || value === null || value === "") return "Unknown";

  const str = String(value).trim().toUpperCase();

  // Direct match: "P1" through "P5"
  if (/^P[1-5]$/.test(str)) return str as PriorityLevel;

  // Numeric: 1-5
  const num = parseInt(str, 10);
  if (num >= 1 && num <= 5) return `P${num}` as PriorityLevel;

  // "Priority 1" / "Priority1" variants
  const match = str.match(/PRIORITY\s*(\d)/);
  if (match && Number(match[1]) >= 1 && Number(match[1]) <= 5) {
    return `P${match[1]}` as PriorityLevel;
  }

  return "Unknown";
}

/**
 * Return hex color for a priority level string.
 * Accepts "P1"-"P5" in any case, or numeric 1-5.
 */
export function priorityColor(level: string | number | undefined): string {
  return PRIORITY_HEX[normalizePriority(level)];
}

/**
 * Return RGBA color for a priority level (for deck.gl ScatterplotLayer).
 */
export function priorityToRGBA(level: string | number | undefined): [number, number, number, number] {
  return PRIORITY_RGBA[normalizePriority(level)];
}

/**
 * Check whether a priority level is "critical" (P4 or P5).
 * Used by stats/KPI calculations.
 */
export function isCriticalPriority(level: string | number | undefined): boolean {
  const normalized = normalizePriority(level);
  return normalized === "P4" || normalized === "P5";
}

/**
 * Detect whether a set of risk/priority labels uses the P1-P5 system
 * rather than the Low/Medium/High/Extreme RMS system.
 */
export function isPrioritySystem(labels: string[]): boolean {
  return labels.some((l) => /^P[1-5]$/i.test(l.trim()));
}
